import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { searchApi, activityApi } from '../services/api';
import {
  LayoutDashboard, ArrowRightLeft, FileText, LogOut, Shield, Menu, X, Settings, Users,
  Zap, FileCheck, Crown, Mail, MessageSquare, Megaphone, Search, Bell, Clock, Eye, EyeOff
} from 'lucide-react';
import { Input } from '../components/ui/input';

/* ====== PRIVACY CONTEXT ====== */
const PrivacyContext = React.createContext({ hidden: false, toggle: () => {} });
export const usePrivacy = () => React.useContext(PrivacyContext);

/* ====== GLOBAL SEARCH ====== */
const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const res = await searchApi.search(q); setResults(res.data.results || []); setOpen(true); } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    setQuery(e.target.value);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(e.target.value), 300);
  };

  const typeLabels = { transfer: 'Virement', transfer_v2: 'V2', cheque: 'Cheque', invoice: 'Facture', user: 'Utilisateur' };

  return (
    <div ref={ref} className="relative w-full max-w-md" data-testid="global-search">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input placeholder="Rechercher..." value={query} onChange={handleChange} onFocus={() => results.length > 0 && setOpen(true)}
        className="pl-10 h-11 rounded-full bg-slate-50 border-slate-200 focus:bg-white text-sm" data-testid="global-search-input" />
      {loading && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />}
      {open && results.length > 0 && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 max-h-72 overflow-y-auto" data-testid="search-results">
          {results.map((r, i) => (
            <button key={i} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0" onClick={() => { navigate(r.route); setOpen(false); setQuery(''); }}>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">{typeLabels[r.type] || r.type}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ====== NOTIFICATION BELL ====== */
const NotificationBell = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try { const r = await activityApi.getRecent(); setPendingCount(r.data.pending_count || 0); setEvents(r.data.events || []); } catch {}
  }, []);

  useEffect(() => { fetch(); const i = setInterval(fetch, 30000); return () => clearInterval(i); }, [fetch]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const statusColors = { pending: 'text-amber-600', executed: 'text-emerald-600', rejected: 'text-red-600' };
  const statusLabels = { pending: 'En attente', executed: 'Execute', rejected: 'Rejete' };
  const typeRoutes = { transfer: '/transfers', transfer_v2: '/transfers-v2', cheque: '/cheques' };

  return (
    <div ref={ref} className="relative" data-testid="notification-panel">
      <button onClick={() => setOpen(!open)} className="relative w-10 h-10 rounded-full bg-[#F5C518] flex items-center justify-center" data-testid="notification-bell">
        <Bell className="w-4.5 h-4.5 text-slate-900" strokeWidth={2} />
        {pendingCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold bg-red-500 text-white flex items-center justify-center">{pendingCount > 99 ? '99+' : pendingCount}</span>}
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-slate-100"><p className="text-sm font-bold text-slate-900">Activite recente</p></div>
          {events.length === 0 ? <div className="p-6 text-center text-sm text-slate-400">Aucune activite</div> :
            events.slice(0, 12).map((e, i) => (
              <button key={i} className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0" onClick={() => { navigate(typeRoutes[e.type] || '/dashboard'); setOpen(false); }}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-900 truncate flex-1">{e.title}</p>
                  <span className={`text-[10px] font-semibold ml-2 ${statusColors[e.status] || ''}`}>{statusLabels[e.status] || e.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{e.amount?.toLocaleString('fr-FR')} {e.currency}</p>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

/* ====== PRO BADGE ====== */
const ProBadge = () => (
  <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#F5C518] text-slate-900 leading-none">PRO</span>
);

/* ====== SIDEBAR (Desktop) ====== */
const Sidebar = () => {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';

  const nav = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/transfers', icon: ArrowRightLeft, label: 'Virements' },
    { to: '/transfers-v2', icon: Zap, label: 'Virement V2', pro: true },
    { to: '/cheques', icon: FileCheck, label: 'Cheques', pro: true },
    { to: '/invoices', icon: FileText, label: 'Factures' },
    { to: '/email', icon: Mail, label: 'Email pro' },
    { to: '/sms', icon: MessageSquare, label: 'SMS', pro: true },
  ];
  const admin_nav = isAdmin ? [
    { to: '/users', icon: Users, label: 'Utilisateurs' },
    { to: '/audit-logs', icon: Shield, label: "Journal d'audit" },
    { to: '/announcements', icon: Megaphone, label: 'Annonces', badge: 'Nouveau' },
    { to: '/settings', icon: Settings, label: 'Parametres', highlight: true },
  ] : [
    { to: '/settings', icon: Settings, label: 'Parametres' },
  ];

  const renderItem = (item) => (
    <li key={item.to}>
      <NavLink to={item.to}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
            isActive ? 'bg-slate-950 text-white' :
            item.highlight ? 'bg-[#FEF9C3] text-slate-900 hover:bg-[#FDE047]' :
            'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`
        }>
        <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
        <span>{item.label}</span>
        {item.pro && <ProBadge />}
        {item.badge && <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white">{item.badge}</span>}
      </NavLink>
    </li>
  );

  return (
    <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col h-screen fixed left-0 top-0 z-40" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#F5C518] rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-slate-900" strokeWidth={2} />
          </div>
          <span className="font-heading font-bold text-base text-slate-900 tracking-tight">WorldPay</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1">
        <ul className="space-y-0.5">{nav.map(renderItem)}</ul>
        <div className="my-3 border-t border-slate-100" />
        <ul className="space-y-0.5">{admin_nav.map(renderItem)}</ul>
      </nav>
      <div className="px-4 py-4 border-t border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
            {admin?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{admin?.name}</p>
            <div className="flex items-center gap-1.5">
              <p className="text-[11px] text-slate-500 truncate">{admin?.email}</p>
              {admin?.account_type === 'premium' && <Crown className="w-3 h-3 text-[#F5C518] shrink-0" />}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

/* ====== FULL SCREEN MENU (Mobile) ====== */
const FullMenu = ({ open, onClose }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = admin?.role === 'admin';

  const items = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/transfers', icon: ArrowRightLeft, label: 'Virements' },
    { to: '/transfers-v2', icon: Zap, label: 'Virement V2', pro: true },
    { to: '/cheques', icon: FileCheck, label: 'Cheques', pro: true },
    { to: '/invoices', icon: FileText, label: 'Factures' },
    { to: '/email', icon: Mail, label: 'Email pro' },
    { to: '/sms', icon: MessageSquare, label: 'SMS', pro: true },
    ...(isAdmin ? [
      { to: '/users', icon: Users, label: 'Utilisateurs' },
      { to: '/audit-logs', icon: Shield, label: "Journal d'audit" },
      { to: '/announcements', icon: Megaphone, label: 'Annonces', badge: 'Nouveau' },
    ] : []),
    { to: '/settings', icon: Settings, label: 'Parametres', highlight: true, badge: 'Nouveau' },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col" data-testid="full-menu">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#F5C518] rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-slate-900" strokeWidth={2} />
          </div>
          <span className="font-heading font-bold text-base text-slate-900">WorldPay</span>
          {admin?.account_type === 'premium' && <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#F5C518] text-slate-900">{admin?.account_type === 'premium' ? 'Premium' : 'Standard'}</span>}
        </div>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-600" /></button>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-3">
        {items.map(item => (
          <NavLink key={item.to} to={item.to} onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-[15px] font-medium transition-colors ${
                isActive ? 'bg-slate-950 text-white' :
                item.highlight ? 'bg-[#FEF9C3] text-slate-900' :
                'text-slate-700 hover:bg-slate-50'
              }`
            }>
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{item.label}</span>
            {item.pro && <ProBadge />}
            {item.badge && <span className="ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{item.badge}</span>}
          </NavLink>
        ))}
        <div className="my-3 border-t border-slate-100" />
        <button onClick={() => { logout(); navigate('/login'); onClose(); }}
          className="flex items-center gap-3.5 px-3 py-3.5 rounded-xl text-[15px] font-medium text-red-600 hover:bg-red-50 w-full">
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Deconnexion</span>
        </button>
      </nav>
    </div>
  );
};

/* ====== BOTTOM BAR (Mobile) ====== */
const BottomBar = ({ onMenuOpen }) => {
  const location = useLocation();
  const items = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { to: '/transfers', icon: ArrowRightLeft, label: 'Virements' },
    { to: '/invoices', icon: FileText, label: 'Factures' },
    { to: '/users', icon: Users, label: 'Utilisateurs' },
  ];

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-slate-200 flex items-center justify-around z-40 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]" data-testid="bottom-bar">
      {items.map(item => {
        const active = location.pathname === item.to;
        return (
          <NavLink key={item.to} to={item.to} className="flex flex-col items-center gap-0.5 px-2 py-1">
            <item.icon className={`w-5 h-5 ${active ? 'text-slate-900' : 'text-slate-400'}`} strokeWidth={active ? 2 : 1.5} />
            <span className={`text-[10px] font-medium ${active ? 'text-slate-900' : 'text-slate-400'}`}>{item.label}</span>
          </NavLink>
        );
      })}
      <button onClick={onMenuOpen} className="flex flex-col items-center gap-0.5 px-2 py-1">
        <Menu className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
        <span className="text-[10px] font-medium text-slate-400">Menu</span>
      </button>
    </div>
  );
};

/* ====== LAYOUT ====== */
export const Layout = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [privacyHidden, setPrivacyHidden] = useState(false);

  return (
    <PrivacyContext.Provider value={{ hidden: privacyHidden, toggle: () => setPrivacyHidden(p => !p) }}>
      <div className="min-h-screen bg-[#FAFAFC]">
        <Sidebar />
        <FullMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

        {/* Top bar */}
        <div className="lg:ml-64 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-30" data-testid="top-bar">
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-7 h-7 bg-[#F5C518] rounded-lg flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-slate-900" strokeWidth={2} />
            </div>
            <span className="font-heading font-bold text-sm text-slate-900">WorldPay</span>
          </div>
          <div className="hidden lg:block flex-1 max-w-md"><GlobalSearch /></div>
          <div className="flex items-center gap-2.5">
            <button onClick={() => setPrivacyHidden(p => !p)} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center" data-testid="privacy-toggle">
              {privacyHidden ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
            </button>
            <NotificationBell />
          </div>
        </div>

        <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          {children}
        </main>

        <BottomBar onMenuOpen={() => setMenuOpen(true)} />
      </div>
    </PrivacyContext.Provider>
  );
};

export default Layout;
